import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import * as XLSX from "xlsx";
import FontAwesome from "react-fontawesome";
import Loading from "../../common/components/loading/loading";
import SearchBox from "../../common/components/search/searchBox";
import Input from "../../common/components/form/input";
import Select from "../../common/components/form/select";
import { formatDate } from "../../common/services/utilities";
import IssuesChart from "../albertaEnvironmental/tradesChart";

function toDateInputValue(date) {
  return new Date(date).toLocaleDateString("fr-CA");
}

function hasValidDate(value) {
  return !Number.isNaN(Date.parse(value));
}

function buildSelectOptions(records, field, optionField) {
  return [{ [optionField]: "All" }].concat(
    [...new Set(records.map((record) => record[field]).filter(Boolean))]
      .sort((left, right) =>
        String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: "base",
        })
      )
      .map((value) => ({ [optionField]: value }))
  );
}

const BCEnvironmentalActivityBase = ({
  t,
  titleKey,
  retrievedCountKey,
  chartTitle,
  downloadFileName,
  fetchData,
  dateField,
  searchFields,
  exportMapper,
  TableComponent,
  quantityField = "holdingQuantity",
}) => {
  const [records, setRecords] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [projectType, setProjectType] = useState("All");
  const [projectTypes, setProjectTypes] = useState([]);
  const [vintage, setVintage] = useState("All");
  const [vintages, setVintages] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      const response = await fetchData();

      if (!isMounted) {
        return;
      }

      const validDates = response
        .map((record) => record[dateField])
        .filter(hasValidDate)
        .map((value) => new Date(value));

      if (validDates.length > 0) {
        const minRecordDate = new Date(
          Math.min(...validDates.map((value) => value.getTime()))
        );
        const maxRecordDate = new Date(
          Math.max(...validDates.map((value) => value.getTime()))
        );
        const defaultStartDate = new Date(maxRecordDate);
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);

        setMinDate(toDateInputValue(minRecordDate));
        setMaxDate(toDateInputValue(maxRecordDate));
        setStartDate(
          toDateInputValue(
            defaultStartDate < minRecordDate ? minRecordDate : defaultStartDate
          )
        );
        setEndDate(toDateInputValue(maxRecordDate));
      }

      setRecords(response);
      setProjectTypes(buildSelectOptions(response, "projectType", "projectType"));
      setVintages(buildSelectOptions(response, "vintage", "vintage"));
      setIsLoading(false);
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [dateField, fetchData]);

  const filteredRecords = useMemo(() => {
    let filtered = [...records];

    if (startDate && endDate) {
      filtered = filtered.filter((record) => {
        if (!hasValidDate(record[dateField])) {
          return false;
        }

        const recordDate = new Date(record[dateField]);
        return (
          recordDate >= new Date(startDate + "T00:00:00") &&
          recordDate <= new Date(endDate + "T23:59:59")
        );
      });
    }

    if (projectType !== "All") {
      filtered = filtered.filter((record) => record.projectType === projectType);
    }

    if (vintage !== "All") {
      filtered = filtered.filter(
        (record) => String(record.vintage) === String(vintage)
      );
    }

    if (searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter((record) =>
        searchFields.some((field) =>
          String(record[field] ?? "").toLowerCase().includes(normalizedQuery)
        )
      );
    }

    return _.orderBy(
      filtered,
      [(record) => Date.parse(record[dateField]) || 0],
      ["desc"]
    );
  }, [
    dateField,
    endDate,
    projectType,
    records,
    searchFields,
    searchQuery,
    startDate,
    vintage,
  ]);

  const chartData = useMemo(
    () =>
      filteredRecords.map((record) => ({
        TransactionDate: formatDate(record[dateField]),
        Quantity: Number(record[quantityField] ?? 0),
      })),
    [dateField, filteredRecords, quantityField]
  );

  const handleStartDateChange = (value) => {
    if (!isNaN(Date.parse(value))) {
      setStartDate(value);
    }
  };

  const handleEndDateChange = (value) => {
    if (!isNaN(Date.parse(value))) {
      setEndDate(value);
    }
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredRecords.map((record) => exportMapper(record, formatDate))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, downloadFileName);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12 d-flex align-items-center justify-content-center">
          <h1>{t(titleKey)}</h1>
        </div>
        <div className="col-md-3" style={{ alignSelf: "flex-end" }}>
          <SearchBox value={searchQuery} onChange={setSearchQuery} />
        </div>
        <div className="col-md-2">
          <Input
            type="date"
            min={minDate}
            max={endDate}
            onKeyDown={(event) => event.preventDefault()}
            onFocus={(event) => event.target.blur()}
            onChange={(event) => handleStartDateChange(event.currentTarget.value)}
            value={startDate ?? ""}
            label={t("StartDate")}
          />
        </div>
        <div className="col-md-2">
          <Input
            type="date"
            min={startDate}
            max={maxDate}
            onKeyDown={(event) => event.preventDefault()}
            onFocus={(event) => event.target.blur()}
            onChange={(event) => handleEndDateChange(event.currentTarget.value)}
            value={endDate ?? ""}
            label={t("EndDate")}
          />
        </div>
        <div className="col-md-2">
          <Select
            idField="projectType"
            optionField="projectType"
            name="projectType"
            value={projectType}
            label={t("ProjectType")}
            options={projectTypes}
            onChange={(event) => setProjectType(event.currentTarget.value)}
          />
        </div>
        <div className="col-md-2">
          <Select
            idField="vintage"
            optionField="vintage"
            name="vintage"
            value={vintage}
            label={t("Vintage")}
            options={vintages}
            onChange={(event) => setVintage(event.currentTarget.value)}
          />
        </div>
        <div className="col-md-1" style={{ alignSelf: "flex-end" }}>
          <button className="btn btn-sm btn-secondary" onClick={downloadExcel}>
            <FontAwesome name="download" /> {t("Excel")}
          </button>
        </div>
      </div>
      <p>{t(retrievedCountKey, { count: filteredRecords.length })}</p>
      {startDate && endDate && (
        <div className="col-md-12 tile">
          <IssuesChart
            data={chartData}
            title={chartTitle}
            startDate={startDate}
            endDate={endDate}
          />
        </div>
      )}
      <div className="col-md-12" style={{ paddingBottom: "30px" }}>
        <TableComponent records={filteredRecords} />
      </div>
    </div>
  );
};

export default BCEnvironmentalActivityBase;
